/**
 * Detect Element Resize.
 * https://github.com/sdecima/javascript-detect-element-resize
 * Sebastian Decima
 *
 * Forked from version 0.5.3; includes the following modifications:
 * 1) Guard against unsafe 'window' and 'document' references (to support SSR).
 * 2) Defer initialization code via a top-level function wrapper (to support SSR).
 * 3) Avoid unnecessary reflows by not measuring size for scroll events bubbling from children.
 * 4) Add nonce for style element.
 * 5) Use 'export' statement over 'module.exports' assignment
 * 6) Added type definitions (nross for C99)
 **/

type ResizeListener = (this: HTMLElement, ev: Event) => void;

type ResizeTriggerRoot = HTMLDivElement & {
  __animationListener__?: (e: AnimationEvent) => void;
};

type ElementWithResize = HTMLElement & {
  __resizeTriggers__?: ResizeTriggerRoot;
  __resizeLast__?: { width?: number; height?: number };
  __resizeListeners__?: ResizeListener[];
  __resizeRAF__?: ResizeFrameToken;
};

export type DetectElementResize = {
  addResizeListener: (element: HTMLElement, fn: ResizeListener) => void;
  removeResizeListener: (element: HTMLElement, fn: ResizeListener) => void;
};

type SchedulersHost = Pick<Window, 'clearTimeout' | 'setTimeout'> & {
  cancelAnimationFrame?: Window['cancelAnimationFrame'];
  mozCancelAnimationFrame?: (handle: number) => void;
  webkitCancelAnimationFrame?: (handle: number) => void;
  requestAnimationFrame?: Window['requestAnimationFrame'];
  mozRequestAnimationFrame?: (cb: FrameRequestCallback) => number;
  webkitRequestAnimationFrame?: (cb: FrameRequestCallback) => number;
  getComputedStyle?: Window['getComputedStyle'];
};

let windowObject: SchedulersHost = globalThis;
if (typeof window !== 'undefined') {
  windowObject = window;
} else if (typeof self !== 'undefined') {
  windowObject = self;
}

const TIMEOUT_DURATION = 20;

const setTimeoutFn = windowObject.setTimeout.bind(windowObject) as typeof globalThis.setTimeout;
const clearTimeoutFn = windowObject.clearTimeout.bind(
  windowObject,
) as typeof globalThis.clearTimeout;

type TimerHandle = ReturnType<typeof setTimeoutFn>;
type ResizeFrameToken = TimerHandle | [number, TimerHandle];

const cancelAnimationFrameFn =
  windowObject.cancelAnimationFrame ||
  windowObject.mozCancelAnimationFrame ||
  windowObject.webkitCancelAnimationFrame;

const requestAnimationFrameFn =
  windowObject.requestAnimationFrame ||
  windowObject.mozRequestAnimationFrame ||
  windowObject.webkitRequestAnimationFrame;

let cancelFrame: (token: ResizeFrameToken) => void;
let requestFrame: (callback: () => void) => ResizeFrameToken;

if (cancelAnimationFrameFn == null || requestAnimationFrameFn == null) {
  cancelFrame = function cancelFrameTimeoutOnly(token) {
    clearTimeoutFn(token as TimerHandle);
  };
  requestFrame = function requestAnimationFrameViaSetTimeout(callback) {
    return setTimeoutFn(callback, TIMEOUT_DURATION);
  };
} else {
  const cancelAnim = cancelAnimationFrameFn;
  const requestAnim = requestAnimationFrameFn;
  cancelFrame = function cancelFrameImpl(token) {
    if (Array.isArray(token)) {
      const [animationFrameID, timeoutID] = token;
      cancelAnim(animationFrameID as number);
      clearTimeoutFn(timeoutID);
    } else {
      clearTimeoutFn(token as TimerHandle);
    }
  };
  requestFrame = function requestAnimationFrameWithSetTimeoutFallback(callback) {
    const animationFrameID = requestAnim(function animationFrameCallback() {
      clearTimeoutFn(timeoutID);
      callback();
    });

    const timeoutID = setTimeoutFn(function timeoutCallback() {
      cancelAnim(animationFrameID as number);
      callback();
    }, TIMEOUT_DURATION);

    return [animationFrameID as number, timeoutID];
  };
}

type LegacyDocument = Document & { attachEvent?: (type: string, handler: EventListener) => void };
type LegacyHTMLElement = HTMLElement & {
  attachEvent?: (type: string, handler: EventListener) => void;
  detachEvent?: (type: string, handler: EventListener) => void;
};

function createDetectElementResize(nonce?: string | null): DetectElementResize {
  let animationKeyframes = '';
  let animationName = '';
  let animationStartEvent: string | undefined;
  let animationStyle = '';
  let checkTriggers!: (element: ElementWithResize) => boolean;
  let resetTriggers!: (element: ElementWithResize) => void;
  let scrollListener!: (e: Event) => void;

  const legacyDocument = typeof document !== 'undefined' ? (document as LegacyDocument) : undefined;
  const attachEvent = Boolean(legacyDocument?.attachEvent);

  if (!attachEvent) {
    resetTriggers = function resetTriggersImpl(element) {
      const triggers = element.__resizeTriggers__;
      if (!triggers) {
        return;
      }
      const expand = triggers.firstElementChild as HTMLElement;
      const contract = triggers.lastElementChild as HTMLElement;
      const expandChild = expand.firstElementChild as HTMLElement;
      contract.scrollLeft = contract.scrollWidth;
      contract.scrollTop = contract.scrollHeight;
      expandChild.style.width = expand.offsetWidth + 1 + 'px';
      expandChild.style.height = expand.offsetHeight + 1 + 'px';
      expand.scrollLeft = expand.scrollWidth;
      expand.scrollTop = expand.scrollHeight;
    };

    checkTriggers = function checkTriggersImpl(element) {
      const last = element.__resizeLast__;
      return element.offsetWidth !== last?.width || element.offsetHeight !== last?.height;
    };

    scrollListener = function scrollListenerImpl(e: Event) {
      const element = e.currentTarget as ElementWithResize;
      const rawTarget = e.target;
      let className = '';
      if (rawTarget instanceof Element) {
        if (typeof rawTarget.className === 'string') {
          className = rawTarget.className;
        } else if ('baseVal' in rawTarget.className) {
          className = (rawTarget.className as unknown as { baseVal: string }).baseVal;
        }
      }

      if (
        className &&
        typeof className.indexOf === 'function' &&
        className.indexOf('contract-trigger') < 0 &&
        className.indexOf('expand-trigger') < 0
      ) {
        return;
      }

      resetTriggers(element);
      if (element.__resizeRAF__) {
        cancelFrame(element.__resizeRAF__);
      }
      element.__resizeRAF__ = requestFrame(function animationFrame() {
        if (checkTriggers(element)) {
          element.__resizeLast__ = {
            width: element.offsetWidth,
            height: element.offsetHeight,
          };
          element.__resizeListeners__?.forEach(function forEachResizeListener(fn) {
            fn.call(element, e);
          });
        }
      });
    };

    let animation = false;
    let keyframeprefix = '';
    animationStartEvent = 'animationstart';
    /* eslint-disable lingui/no-unlocalized-strings -- DOM/CSS capability probe strings */
    const domPrefixes = 'Webkit Moz O ms'.split(' ');
    const startEvents =
      'webkitAnimationStart animationstart oAnimationStart MSAnimationStart'.split(' ');
    let pfx = '';
    {
      const elm = document.createElement('fakeelement');
      if (elm.style.animationName !== undefined) {
        animation = true;
      }

      if (animation === false) {
        for (let i = 0; i < domPrefixes.length; i++) {
          const prefixedKey = `${domPrefixes[i]}AnimationName` as keyof CSSStyleDeclaration;
          if (elm.style[prefixedKey] !== undefined) {
            pfx = domPrefixes[i];
            keyframeprefix = `-${pfx.toLowerCase()}-`;
            animationStartEvent = startEvents[i];
            animation = true;
            break;
          }
        }
      }
    }
    /* eslint-enable lingui/no-unlocalized-strings */

    animationName = 'resizeanim';
    animationKeyframes =
      '@' +
      keyframeprefix +
      'keyframes ' +
      animationName +
      ' { from { opacity: 0; } to { opacity: 0; } } ';
    animationStyle = keyframeprefix + 'animation: 1ms ' + animationName + '; ';
  }

  const createStyles = function createStylesImpl(doc: Document) {
    if (!doc.getElementById('detectElementResize')) {
      const css =
          animationKeyframes +
          '.resize-triggers { ' +
          animationStyle +
          'visibility: hidden; opacity: 0; } ' +
          '.resize-triggers, .resize-triggers > div, .contract-trigger:before { content: " "; display: block; position: absolute; top: 0; left: 0; height: 100%; width: 100%; overflow: hidden; z-index: -1; } .resize-triggers > div { background: #eee; overflow: auto; } .contract-trigger:before { width: 200%; height: 200%; }',
        head = doc.head || doc.getElementsByTagName('head')[0],
        style = doc.createElement('style');

      style.id = 'detectElementResize';
      style.type = 'text/css';

      if (nonce != null) {
        style.setAttribute('nonce', nonce);
      }

      const styleWithSheet = style as HTMLStyleElement & {
        styleSheet?: { cssText: string };
      };
      if (styleWithSheet.styleSheet) {
        styleWithSheet.styleSheet.cssText = css;
      } else {
        style.appendChild(doc.createTextNode(css));
      }

      head.appendChild(style);
    }
  };

  const addResizeListener = function addResizeListenerImpl(
    element: HTMLElement,
    fn: ResizeListener,
  ) {
    const el = element as LegacyHTMLElement;
    if (attachEvent) {
      el.attachEvent?.('onresize', fn);
    } else {
      const host = element as ElementWithResize;
      if (!host.__resizeTriggers__) {
        const doc = element.ownerDocument;
        const elementStyle = windowObject.getComputedStyle?.(element);
        if (elementStyle && elementStyle.position === 'static') {
          element.style.position = 'relative';
        }
        createStyles(doc);
        host.__resizeLast__ = {};
        host.__resizeListeners__ = [];
        const triggers = doc.createElement('div') as ResizeTriggerRoot;
        triggers.className = 'resize-triggers';
        host.__resizeTriggers__ = triggers;
        const expandTrigger = doc.createElement('div');
        expandTrigger.className = 'expand-trigger';
        expandTrigger.appendChild(doc.createElement('div'));
        const contractTrigger = doc.createElement('div');
        contractTrigger.className = 'contract-trigger';
        triggers.appendChild(expandTrigger);
        triggers.appendChild(contractTrigger);
        element.appendChild(triggers);
        resetTriggers(host);
        element.addEventListener('scroll', scrollListener, true);

        if (animationStartEvent) {
          triggers.__animationListener__ = function animationListener(e: AnimationEvent) {
            if (e.animationName === animationName) {
              resetTriggers(host);
            }
          };
          triggers.addEventListener(
            animationStartEvent,
            triggers.__animationListener__ as EventListener,
          );
        }
      }
      host.__resizeListeners__?.push(fn);
    }
  };

  const removeResizeListener = function removeResizeListenerImpl(
    element: HTMLElement,
    fn: ResizeListener,
  ) {
    const el = element as LegacyHTMLElement;
    if (attachEvent) {
      el.detachEvent?.('onresize', fn);
    } else {
      const host = element as ElementWithResize;
      const listeners = host.__resizeListeners__;
      if (!listeners) {
        return;
      }
      listeners.splice(listeners.indexOf(fn), 1);
      if (!listeners.length) {
        element.removeEventListener('scroll', scrollListener, true);
        const triggers = host.__resizeTriggers__;
        if (triggers?.__animationListener__) {
          if (animationStartEvent) {
            triggers.removeEventListener(
              animationStartEvent,
              triggers.__animationListener__ as EventListener,
            );
          }
          triggers.__animationListener__ = undefined;
        }
        try {
          const toRemove = host.__resizeTriggers__;
          if (toRemove) {
            element.removeChild(toRemove);
            host.__resizeTriggers__ = undefined;
          }
        } catch {
          // Preact compat; see developit/preact-compat/issues/228
        }
      }
    }
  };

  return {
    addResizeListener,
    removeResizeListener,
  };
}

export { createDetectElementResize };
